// Bridge between the Zakat calculator's assetValues and the Estate / Will
// asset entries. Single source of truth for category mapping + currency
// conversion of weight-based fields (gold / silver grams → currency value).

import { assetFields } from '../config/zakatConfig';
import { secureStorage } from './secureStorage';

const LS_KEY = 'zi_zakat_snapshot_v1';

// Friendly labels (used in the imported asset rows so users immediately
// recognise where each line came from).
const FIELD_LABELS = {
  cash:            'Cash in hand',
  savings:         'Bank savings',
  businessCash:    'Business cash',
  businessBank:    'Business bank account',
  gold:            'Gold',
  silver:          'Silver',
  goldJewellery:   'Gold jewellery',
  silverJewellery: 'Silver jewellery',
  investments:     'General investments',
  stocks:          'Stocks',
  mutualFunds:     'Mutual funds',
  holdings:        'Direct equity holdings',
  shares:          'Private / unlisted shares',
  bonds:           'Bonds / sukuk',
  dividends:       'Pending dividends',
  providentFund:   'Provident fund',
  crypto:          'Cryptocurrency',
  bitcoin:         'Bitcoin',
  ethereum:        'Ethereum',
  altcoins:        'Altcoins',
  stablecoins:     'Stablecoins',
  nfts:            'NFTs',
  inventory:       'Inventory',
  receivables:     'Accounts receivable',
  tradeGoods:      'Trade goods',
  practiceAssets:  'Practice assets',
  rentalIncome:    'Saved rental income',
  propertyForSale: 'Property for sale',
  advances:        'Advances received',
  crops:           'Crops',
  livestock:       'Livestock value',
  farmEquipment:   'Farm equipment for sale',
  landForSale:     'Land for sale',
  camels:          'Camels (head)',
  cattle:          'Cattle (head)',
  sheep:           'Sheep (head)',
  goats:           'Goats (head)',
  poultry:         'Poultry',
  pension:         'Accessible pension',
  gratuity:        'Gratuity',
  mahr:            'Mahr receivable',
  scholarships:    'Scholarship balance',
};

// Map every Zakat field → the inheritance "estate" category bucket.
// Buckets:  realEstate · business · investments · bankCash · retirement
//           movable · receivables
const ESTATE_BUCKET = {
  cash: 'bankCash', savings: 'bankCash', businessCash: 'bankCash', businessBank: 'bankCash',
  gold: 'movable', silver: 'movable', goldJewellery: 'movable', silverJewellery: 'movable',
  investments: 'investments', stocks: 'investments', mutualFunds: 'investments',
  holdings: 'investments', shares: 'investments', bonds: 'investments',
  dividends: 'investments', providentFund: 'retirement',
  crypto: 'investments', bitcoin: 'investments', ethereum: 'investments',
  altcoins: 'investments', stablecoins: 'investments', nfts: 'investments',
  inventory: 'business', receivables: 'receivables', tradeGoods: 'business',
  practiceAssets: 'business',
  rentalIncome: 'bankCash', propertyForSale: 'realEstate', advances: 'bankCash',
  crops: 'business', livestock: 'movable', farmEquipment: 'business', landForSale: 'realEstate',
  pension: 'retirement', gratuity: 'retirement',
  mahr: 'receivables', scholarships: 'bankCash',
};

// Map every Zakat field → the Will section asset categories.
// Categories: property · bank · investments · business · gold · vehicle
//             retirement · receivables · digital · other
const WILL_CATEGORY = {
  cash: 'bank', savings: 'bank', businessCash: 'bank', businessBank: 'bank',
  gold: 'gold', silver: 'gold', goldJewellery: 'gold', silverJewellery: 'gold',
  investments: 'investments', stocks: 'investments', mutualFunds: 'investments',
  holdings: 'investments', shares: 'investments', bonds: 'investments',
  dividends: 'investments', providentFund: 'retirement',
  crypto: 'digital', bitcoin: 'digital', ethereum: 'digital',
  altcoins: 'digital', stablecoins: 'digital', nfts: 'digital',
  inventory: 'business', receivables: 'receivables', tradeGoods: 'business',
  practiceAssets: 'business',
  rentalIncome: 'bank', propertyForSale: 'property', advances: 'bank',
  crops: 'business', livestock: 'other', farmEquipment: 'business', landForSale: 'property',
  pension: 'retirement', gratuity: 'retirement',
  mahr: 'receivables', scholarships: 'bank',
};

// ---------- Snapshot persistence ----------------------------------------

/**
 * Persist a snapshot of the current Zakat calculator inputs so the
 * Inheritance and Will pages can offer to import them.
 *
 * Stored payload (LS key `zi_zakat_snapshot_v1`):
 *   { assetValues, goldPricePerGram, silverPricePerGram, currency, country, at }
 */
export const saveZakatSnapshot = (payload) => {
  try {
    const meaningful = Object.entries(payload?.assetValues || {}).some(
      ([, v]) => Number(v) > 0
    );
    if (!meaningful) {
      secureStorage.removeItem(LS_KEY);
      return;
    }
    secureStorage.setItem(
      LS_KEY,
      JSON.stringify({ ...payload, at: Date.now() })
    );
  } catch (_e) { /* localStorage unavailable */ }
};

export const readZakatSnapshot = () => {
  try {
    const raw = secureStorage.getItem(LS_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_e) {
    return null;
  }
};

export const clearZakatSnapshot = () => {
  try { secureStorage.removeItem(LS_KEY); } catch (_e) { /* noop */ }
};

// ---------- Mapping helpers ----------------------------------------------

/**
 * Convert a Zakat asset entry (key + raw value) to a *currency value*.
 * Weight fields (gold/silver grams) are multiplied by per-gram price.
 * Count fields (camels/cattle/...) are passed through unchanged — they
 * are intentionally low-fidelity here because livestock-by-head is not
 * meaningful for a will / estate valuation (the user can edit later).
 */
const toCurrencyValue = (key, raw, goldPx, silverPx) => {
  const v = Number(raw) || 0;
  if (v <= 0) return 0;
  const def = assetFields[key];
  if (!def) return v;
  if (def.type === 'weight') {
    const px = def.metalType === 'silver' ? silverPx : goldPx;
    return v * (Number(px) || 0);
  }
  return v;
};

/**
 * Convert a Zakat snapshot into a list of Will asset rows.
 *
 * @returns Array<{id, category, label, value, location}>
 *          Each row has a unique id so the Will page can render them.
 */
export const mapZakatToWillAssets = (snapshot) => {
  if (!snapshot || !snapshot.assetValues) return [];
  const goldPx = Number(snapshot.goldPricePerGram) || 0;
  const silverPx = Number(snapshot.silverPricePerGram) || 0;
  const out = [];
  let i = 0;
  for (const [key, raw] of Object.entries(snapshot.assetValues)) {
    const value = toCurrencyValue(key, raw, goldPx, silverPx);
    if (value <= 0) continue;
    out.push({
      id: Date.now() + (i++),
      category: WILL_CATEGORY[key] || 'other',
      label: FIELD_LABELS[key] || key,
      value: Math.round(value),
      location: '',
    });
  }
  return out;
};

/**
 * Convert a Zakat snapshot into a list of Estate (Inheritance) rows.
 *
 * The Estate Step 1 uses one form per bucket. We return a flat list
 * tagged with the bucket id so the page can append rows to the right
 * accordion section. Output items match the `newItem(category)` schema
 * from `/utils/estateValuation.js`.
 *
 * @returns Array<{ bucket, item }> where `item` is ready to drop into
 *          `valuation[bucket]`.
 */
export const mapZakatToEstateRows = (snapshot) => {
  if (!snapshot || !snapshot.assetValues) return [];
  const goldPx = Number(snapshot.goldPricePerGram) || 0;
  const silverPx = Number(snapshot.silverPricePerGram) || 0;
  const out = [];
  let i = 0;
  for (const [key, raw] of Object.entries(snapshot.assetValues)) {
    const value = toCurrencyValue(key, raw, goldPx, silverPx);
    if (value <= 0) continue;
    const bucket = ESTATE_BUCKET[key] || 'movable';
    const rounded = Math.round(value);
    out.push({
      bucket,
      item: {
        id: `${Date.now()}-${i++}-${Math.random().toString(36).slice(2, 6)}`,
        category: bucket,
        label: FIELD_LABELS[key] || key,
        value: rounded,        // legacy field for back-compat
        dodValue: rounded,
        distValue: 0,
        ownership: {
          type: 'sole',
          coOwner: '',
          totalValue: 0,
          totalDodValue: 0,
          totalDistValue: 0,
          deceasedPercent: 100,
          undocumented: false,
        },
        timing: {
          incomeGenerated: false, incomeAmount: 0, incomePeriod: '',
          inKindToHeir: '', willLiquidate: false, disposalCosts: 0,
          heirContribution: 0, heirContributor: '',
          differentCurrency: false, assetCurrency: '', fxBasis: 'settlement',
        },
        meta: {
          propertyType: '', location: '', occupancy: '',
          businessName: '', valuationBasis: '',
          companyType: '', instrumentName: '', instrumentType: '',
          priceDate: '', bankName: '', accountType: '',
          benefitType: '', nominee: '', assetType: '',
          debtor: '', recovery: 'certain',
          policyType: '', includeInEstate: false,
        },
      },
    });
  }
  return out;
};

/**
 * Summary used by the import banner: how many rows would be imported,
 * total value, and the snapshot age in a friendly string.
 */
export const summarizeSnapshot = (snapshot) => {
  if (!snapshot) return null;
  const goldPx = Number(snapshot.goldPricePerGram) || 0;
  const silverPx = Number(snapshot.silverPricePerGram) || 0;
  let rows = 0;
  let total = 0;
  for (const [key, raw] of Object.entries(snapshot.assetValues || {})) {
    const v = toCurrencyValue(key, raw, goldPx, silverPx);
    if (v <= 0) continue;
    rows += 1;
    total += v;
  }
  const ageMs = Date.now() - (snapshot.at || 0);
  let age = 'just now';
  if (ageMs > 60_000) {
    const m = Math.floor(ageMs / 60_000);
    if (m < 60) age = `${m} min ago`;
    else if (m < 1440) age = `${Math.floor(m / 60)} h ago`;
    else age = `${Math.floor(m / 1440)} d ago`;
  }
  return { rows, total: Math.round(total), age, currency: snapshot.currency || 'USD' };
};
